import { z } from 'zod';
import { demographicsSchema } from './contacts.js';
/**
 * ehr#661 - Preferred contact method must have something to reach.
 *
 * Contact Info's "Preferred contact method" names one of the client's own fields: Mobile phone and
 * Text / SMS need the mobile number, Home phone and Work phone their numbers, Email the email.
 * Picking one whose field is blank sends reminders and outreach nowhere.
 *
 * Blank means the default, Mobile phone (ehr#233, Jay 10-09: the drawer shows it and the
 * reminder projection saves it), so a blank method with no mobile number is the same gap.
 *
 * Block or warn (so staff are never locked out of a chart that already has the gap):
 * - a save that CHANGES the method to one with no value, or CLEARS the value of the method it
 *   keeps, is refused (preferredContactBlock / demographicsSaveSchema);
 * - a chart that already had the gap and keeps it (same method, value still blank) saves, and
 *   the chart shows the warning (preferredContactGap) until someone fixes it.
 * Nothing here changes stored data or consent answers.
 */
export const PREFERRED_CONTACT_DEFAULT = 'Mobile phone';
/** Each method -> the Contact Info field it needs, that field's label, and what it holds. */
const METHOD_FIELD = {
    'mobile phone': { field: 'mobilePhone', label: 'Mobile phone', noun: 'mobile number' },
    'text / sms': { field: 'mobilePhone', label: 'Mobile phone', noun: 'mobile number' },
    'home phone': { field: 'phone', label: 'Home phone', noun: 'home phone number' },
    'work phone': { field: 'workPhone', label: 'Work phone', noun: 'work phone number' },
    email: { field: 'email', label: 'Email', noun: 'email address' },
};
const asObj = (v) => v && typeof v === 'object' && !Array.isArray(v) ? v : {};
const str = (v) => (typeof v === 'string' ? v.trim() : '');
/** The method in force: the picked one, or the default when blank. */
export function effectivePreferredContact(contact) {
    return str(asObj(contact).preferredContact) || PREFERRED_CONTACT_DEFAULT;
}
/**
 * The gap in a Contact Info section (`demographics.contact`): the preferred method's field is
 * blank. Null when it is filled, or the method is one this check does not know (an old value).
 */
export function preferredContactGap(contact) {
    const c = asObj(contact);
    const picked = str(c.preferredContact);
    const method = picked || PREFERRED_CONTACT_DEFAULT;
    const need = METHOD_FIELD[method.toLowerCase()];
    if (!need || str(c[need.field]))
        return null;
    return {
        method,
        defaulted: !picked,
        field: need.field,
        fieldLabel: need.label,
        message: `Preferred contact is ${method}, but no ${need.noun} is on file.`,
        fillIn: `Fill in ${need.label}, or pick another preferred contact method.`,
    };
}
/**
 * The gap this save would CREATE (block it), comparing the stored Contact Info with the new one:
 * the method changed to one with no value, or the method's value was cleared. Null when there is
 * no gap after the save, or the chart already had this same gap (warn only).
 */
export function preferredContactBlock(before, after) {
    const gap = preferredContactGap(after);
    if (!gap)
        return null;
    const changedMethod = effectivePreferredContact(before).toLowerCase() !== gap.method.toLowerCase();
    const clearedValue = Boolean(str(asObj(before)[gap.field]));
    return changedMethod || clearedValue ? gap : null;
}
/**
 * The Demographics payload schema for one save, given what is stored now (`before` = the stored
 * `contact` section, or undefined for a chart with none). The route's demographicsSchema only
 * checks the shape; this adds the ehr#661 rule, which needs the stored record to tell a new gap
 * (refused) from one the chart already had (allowed, warned on the chart).
 */
export function demographicsSaveSchema(before) {
    return demographicsSchema.superRefine((payload, ctx) => {
        const gap = preferredContactBlock(before, payload.contact);
        if (gap)
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['contact', 'preferredContact'],
                message: `${gap.message} ${gap.fillIn}`,
                params: { field: gap.field },
            });
    });
}
//# sourceMappingURL=preferred-contact.js.map